import React, { useMemo, useState } from "react";
import { Dropdown, Modal, Input, message as antdMessage } from "antd";
import type { MenuProps } from "antd";
import { Edit, Trash2, Pin, Reply, Share2, Smile, MoreHorizontal, PinOff, CheckCircle, Eye } from "lucide-react";
import { ChatMessage } from "../../types/chat";
import { useUpdateMessage } from "../../hooks/mutations/useUpdateMessage";
import { useDeleteMessage } from "../../hooks/mutations/useDeleteMessage";
import { usePinMessage } from "../../hooks/mutations/usePinMessage";
import ShareMessageModal from "../ShareMessageModal";
import styles from "./index.module.scss";
import { useGetPinnedMessageIds } from "../../hooks/queries/useGetPinnedMessageIds";

interface MessageActionsProps {
  message: ChatMessage;
  isOwnMessage: boolean;
  roomId: string;
  threadId?: string;
  onReply?: (message: ChatMessage) => void;
  onShare?: (message: ChatMessage) => void;
  onShowReactions?: (message: ChatMessage) => void;
  onShowConfirmations?: () => void;
  onShowReaders?: () => void;
  needConfirmed?: boolean;
  isChannelRoom?: boolean; // Only show confirmation for Channel rooms
  isPinned?: boolean;
  isAdmin?: boolean; // Only admin can see confirmation list
}

export const MessageActions: React.FC<MessageActionsProps> = ({
  message,
  isOwnMessage,
  roomId,
  threadId,
  onReply,
  onShowReactions,
  onShowConfirmations,
  onShowReaders,
  needConfirmed = false,
  isChannelRoom = false,
  isPinned = false,
  isAdmin = false,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editContent, setEditContent] = useState(message.content?.message || "");
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const { data: pinnedData } = useGetPinnedMessageIds(roomId);

  const { mutate: updateMessage } = useUpdateMessage(roomId, threadId);
  const { mutate: deleteMessage } = useDeleteMessage(roomId, threadId);
  const { mutate: pinMessage } = usePinMessage(roomId);

  const isDeleted = message.content?.message === "Tin nhắn đã xóa";
  const isTextMessage = message.type === "text" && !message.content?.message?.match(/\.(jpeg|jpg|png|webp|mp4|pdf)$/i);

  const handleEdit = () => {
    setEditContent(message.content?.message || "");
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editContent.trim()) {
      antdMessage.error("Nội dung tin nhắn không được để trống");
      return;
    }

    updateMessage(
      {
        roomId,
        messageId: message.id || "",
        content: editContent.trim(),
      },
      {
        onSuccess: () => {
          setIsEditModalOpen(false);
          antdMessage.success("Đã cập nhật tin nhắn");
        },
      },
    );
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Xóa tin nhắn",
      content: "Bạn có chắc chắn muốn xóa tin nhắn này?",
      okText: "Xóa",
      cancelText: "Hủy",
      okButtonProps: {
        danger: true,
        style: {
          borderRadius: "8px",
        },
      },
      cancelButtonProps: {
        style: {
          borderRadius: "8px",
        },
      },
      centered: true,
      className: styles.confirmModal,
      onOk: () => {
        deleteMessage({
          roomId,
          messageId: message.id || "",
        });
      },
    });
  };

  const handlePin = () => {
    pinMessage({
      messageId: message.id || "",
      pin: !isPinnedMessage,
      roomId,
    });
  };

  const isPinnedMessage = useMemo(() => {
    const pinData = (pinnedData as any)?.Data;
    return Array.isArray(pinData) && pinData.includes(message.id || "");
  }, [pinnedData, message.id]);

  // Debug: Check needConfirmed prop
  console.log("🔍 MessageActions needConfirmed:", {
    messageId: message.id,
    needConfirmed,
    isOwnMessage,
    isDeleted,
    willShowMenu: !isDeleted && needConfirmed,
  });

  // Build menu items based on message ownership - only Edit, Delete, Reactions, and Pin
  const menuItems: MenuProps["items"] = isOwnMessage
    ? [
        // Own messages: Edit, Delete, Reactions, Pin
        isTextMessage && !isDeleted
          ? {
              key: "edit",
              label: (
                <div className={styles.menuItem}>
                  <Edit size={16} />
                  <span>Chỉnh sửa tin nhắn</span>
                </div>
              ),
              onClick: handleEdit,
            }
          : null,

        !isDeleted
          ? {
              key: "delete",
              label: (
                <div className={styles.menuItem}>
                  <Trash2 size={16} />
                  <span>Xóa tin nhắn</span>
                </div>
              ),
              onClick: handleDelete,
            }
          : null,

        !isDeleted
          ? {
              key: "reactions",
              label: (
                <div className={styles.menuItem}>
                  <Smile size={16} />
                  <span>Danh sách cảm xúc</span>
                </div>
              ),
              onClick: () => onShowReactions?.(message),
            }
          : null,

        !isDeleted
          ? {
              key: "pin",
              label: (
                <div className={styles.menuItem}>
                  {isPinnedMessage ? <PinOff size={16} /> : <Pin size={16} />}
                  <span>{isPinnedMessage ? "Bỏ ghim tin nhắn" : "Ghim tin nhắn"}</span>
                </div>
              ),
              onClick: handlePin,
            }
          : null,

        // Confirmation list - only for Channel rooms and admin users
        !isDeleted && isChannelRoom && isAdmin
          ? {
              key: "confirmations-own",
              label: (
                <div className={styles.menuItem}>
                  <CheckCircle size={16} />
                  <span>Danh sách xác nhận</span>
                </div>
              ),
              onClick: () => onShowConfirmations?.(),
            }
          : null,

        // Reader list - for all messages
        !isDeleted
          ? {
              key: "readers-own",
              label: (
                <div className={styles.menuItem}>
                  <Eye size={16} />
                  <span>Danh sách người xem</span>
                </div>
              ),
              onClick: () => onShowReaders?.(),
            }
          : null,
      ].filter((item): item is NonNullable<typeof item> => item !== null)
    : [
        // Other's messages: Reactions, Pin
        !isDeleted
          ? {
              key: "reactions",
              label: (
                <div className={styles.menuItem}>
                  <Smile size={16} />
                  <span>Danh sách cảm xúc</span>
                </div>
              ),
              onClick: () => onShowReactions?.(message),
            }
          : null,

        !isDeleted
          ? {
              key: "pin",
              label: (
                <div className={styles.menuItem}>
                  {isPinnedMessage ? <PinOff size={16} /> : <Pin size={16} />}
                  <span>{isPinnedMessage ? "Bỏ ghim tin nhắn" : "Ghim tin nhắn"}</span>
                </div>
              ),
              onClick: handlePin,
            }
          : null,

        // Confirmation list - only for Channel rooms and admin users
        !isDeleted && isChannelRoom && needConfirmed && isAdmin
          ? {
              key: "confirmations",
              label: (
                <div className={styles.menuItem}>
                  <CheckCircle size={16} />
                  <span>Danh sách xác nhận</span>
                </div>
              ),
              onClick: () => onShowConfirmations?.(),
            }
          : null,

        // Reader list - for all messages
        !isDeleted
          ? {
              key: "readers",
              label: (
                <div className={styles.menuItem}>
                  <Eye size={16} />
                  <span>Danh sách người xem</span>
                </div>
              ),
              onClick: () => onShowReaders?.(),
            }
          : null,
      ].filter((item): item is NonNullable<typeof item> => item !== null);

  return (
    <>
      <div className={styles.actionsWrapper}>
        {/* Reply Button */}
        {!isDeleted && (
          <div className={styles.actionButton} onClick={() => onReply?.(message)} title="Trả lời">
            <Reply size={16} />
          </div>
        )}

        {/* Share Button */}
        {!isDeleted && (
          <div className={styles.actionButton} onClick={() => setIsShareModalOpen(true)} title="Chia sẻ">
            <Share2 size={16} />
          </div>
        )}

        {/* More Actions Dropdown */}
        <Dropdown
          menu={{ items: menuItems }}
          trigger={["click"]}
          placement={isOwnMessage ? "bottomLeft" : "bottomRight"}
        >
          <div className={styles.actionButton}>
            <MoreHorizontal size={16} />
          </div>
        </Dropdown>
      </div>

      {/* Edit Modal */}
      <Modal
        title="Chỉnh sửa tin nhắn"
        open={isEditModalOpen}
        onOk={handleSaveEdit}
        onCancel={() => setIsEditModalOpen(false)}
        okText="Lưu"
        cancelText="Hủy"
        centered
        className={styles.editModal}
        okButtonProps={{
          style: {
            borderRadius: "8px",
            background: "var(--chat-primary-500)",
          },
        }}
        cancelButtonProps={{
          style: {
            borderRadius: "8px",
          },
        }}
      >
        <Input.TextArea
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          rows={4}
          placeholder="Nhập nội dung tin nhắn..."
          style={{
            borderRadius: "8px",
            borderColor: "var(--chat-border-light)",
          }}
          className={styles.editModalTextArea}
        />
      </Modal>

      {/* Share Message Modal */}
      <ShareMessageModal
        message={message}
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        currentRoomId={roomId}
      />
    </>
  );
};
